'use client';

import { SubmitButton, CancelButton } from '@/components/ui/buttons';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { useCreateWorkspace } from '@/hooks/useWorkspaceQuery';
import { usePricingInfo } from '@/hooks/usePricingQuery';
import { IWorkspaceForm } from '@/types/workspace';
import { handleShowError } from '@/utils';
import { zodResolver } from '@hookform/resolvers/zod';
import { FC } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import * as z from 'zod';

interface ICreateWorkspaceModalProps {
  open: boolean;
  isFirstWorkspace?: boolean;
  onOpenChange: (open: boolean) => void;
}

// Validation schema
const createWorkspaceSchema = z.object({
  name: z
    .string()
    .min(1, 'Workspace name is required')
    .max(255, 'Workspace name must be less than 255 characters')
    .trim(),
  description: z.string().max(500, 'Description must be less than 500 characters').optional(),
});

const CreateWorkspaceModal: FC<ICreateWorkspaceModalProps> = ({
  open,
  isFirstWorkspace = false,
  onOpenChange,
}) => {
  const { data: pricingInfo } = usePricingInfo();
  const { mutate: createWorkspace, isPending } = useCreateWorkspace();

  const form = useForm<IWorkspaceForm>({
    resolver: zodResolver(createWorkspaceSchema),
    defaultValues: {
      name: '',
      description: '',
    },
  });

  const onSubmit = (data: IWorkspaceForm) => {
    createWorkspace(
      {
        name: data.name.trim(),
        description: data.description?.trim(),
      },
      {
        onSuccess: (response) => {
          toast.success(response.message);
          form.reset();
          onOpenChange(false);
          // The workspace list will be automatically refetched by React Query
        },
        onError: (error) => {
          handleShowError(error);
        },
      },
    );
  };

  const handleOpenChange = (newOpen: boolean) => {
    if (!newOpen) {
      form.reset();
    }
    onOpenChange(newOpen);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className='sm:max-w-[425px]'>
        <DialogHeader>
          <DialogTitle>
            {isFirstWorkspace ? 'Create Your First Workspace' : 'Create New Workspace'}
          </DialogTitle>
          <DialogDescription>
            {isFirstWorkspace
              ? `Welcome to marketbetter! Create your first workspace to get started with a 60-day free trial (500K AI + 100 enrichment credits).`
              : "Create a new workspace for your team. You'll be the admin of this workspace."}
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className='space-y-4'>
            <FormField
              control={form.control}
              name='name'
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Workspace Name</FormLabel>
                  <FormControl>
                    <Input placeholder='e.g., Marketing Team' {...field} disabled={isPending} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name='description'
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Description (Optional)</FormLabel>
                  <FormControl>
                    <Textarea
                      placeholder='What is this workspace for?'
                      rows={3}
                      {...field}
                      disabled={isPending}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <DialogFooter>
              {!isFirstWorkspace && (
                <CancelButton
                  onClick={() => handleOpenChange(false)}
                  disabled={isPending}
                >
                  Cancel
                </CancelButton>
              )}
              <SubmitButton
                isLoading={isPending}
                className={isFirstWorkspace ? 'w-full' : ''}
              >
                {isFirstWorkspace ? 'Get Started' : 'Create Workspace'}
              </SubmitButton>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};

export default CreateWorkspaceModal;
